import { useEffect } from 'react';
import { useWorldStore } from '../../state/worldStore';
import { useMoment, loadMoment, stageMoment, momentPose, momentFail, momentBridge, MOMENT_ID } from '../../city/moment';
import { introBridge } from '../../state/introBridge';
import { pendingLoads } from '../../state/bootProgress';

// MomentBoot — a moment's still, OUTSIDE the canvas (city/moment).
//
// Everything that must happen even while the 3D scene is still loading: fetch
// the moment, stage it once the venues have anchors, land the arrival on the
// frame, and WATCH. The scene is one suspense boundary — while any model in it
// is loading, nothing in it runs, so a director inside it could neither say it
// was stuck nor why (caught 2026-11-01: a blank page, no error, until render-bot
// gave up). This logs what it is waiting on every few seconds, and when time
// runs out says exactly that in `window.__shotError`, so the job fails with a
// reason. MomentDirector (inside the canvas) holds the camera and says ready.
const GIVE_UP_MS = 280000;     // render-bot is asked to wait 290 s (a cold first load of the city is slow headless)
const LOG_EVERY_MS = 5000;

export default function MomentBoot() {
  const state = useMoment((s) => s.state);
  const venueAnchors = useWorldStore((s) => s.venueAnchors);

  useEffect(() => { if (MOMENT_ID) loadMoment(); }, []);

  useEffect(() => {
    if (state !== 'ready' || !venueAnchors) return;
    stageMoment(Date.now());
  }, [state, venueAnchors]);

  // The arrival lands on the frame at once (no descent): street zoom, the walk standing down.
  useEffect(() => {
    if (state !== 'staged') return undefined;
    const iv = setInterval(() => {
      const p = momentPose(window.innerWidth / Math.max(1, window.innerHeight));
      if (!p) return;
      clearInterval(iv);
      const w = useWorldStore.getState();
      w.setStreetArrival({ pos: p.pos, look: p.look });
      introBridge.skip = true;
      if (w.charSelect) w.setCharSelect(false);
      w.setEntered(true);
    }, 250);
    return () => clearInterval(iv);
  }, [state]);

  // The watchdog.
  useEffect(() => {
    if (!MOMENT_ID) return undefined;
    let lastLog = 0;
    const iv = setInterval(() => {
      const st = useMoment.getState().state;
      if (st === 'shot' || st === 'error') { clearInterval(iv); return; }
      const t = performance.now();
      const missing = Object.entries(momentBridge.checks).filter(([, v]) => !v).map(([k]) => k);
      const why = st !== 'staged' ? `the moment is ${st}${st === 'ready' && !useWorldStore.getState().venueAnchors ? ' (waiting on the venues\' anchors — the city model is still loading)' : ''}`
        : !Object.keys(momentBridge.checks).length ? 'the 3D scene has not started (a model in it is still loading)'
          : missing.length ? `waiting on ${missing.join(', ')}` : `steady ${momentBridge.steady} frame(s)`;
      if (t - lastLog > LOG_EVERY_MS) {
        lastLog = t;
        const loads = pendingLoads().slice(0, 3).map((l) => `${l.url.split('?')[0].split('/').pop()} ${Math.round(l.ms / 1000)}s`).join(', ');
        console.log(`[moment] ${Math.round(t / 1000)}s: ${why}${loads ? ` · loading: ${loads}` : ''}`);
      }
      if (t > GIVE_UP_MS) { clearInterval(iv); momentFail(`not ready after ${Math.round(t / 1000)} s: ${why}`); }
    }, 1000);
    return () => clearInterval(iv);
  }, []);

  return null;
}
